import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { LazyLoadEvent, SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { AcanCellDirective } from './acan-cell.directive';
import { AcanColumn, AcanTableConfig } from './acan-table.types';

/** Stable default — avoid a new array every change-detection cycle. */
const DEFAULT_ROWS_PER_PAGE_OPTIONS: number[] = [10, 25, 50];

@Component({
  selector: 'acan-table',
  templateUrl: './acan-table.component.html',
  styleUrls: ['./acan-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AcanTableComponent implements AfterContentInit {
  @Input() config: AcanTableConfig = { columns: [] };
  @Input() value: any[] = [];
  @Input() totalRecords = 0;
  @Input() selection: any;

  @Output() lazyLoad = new EventEmitter<LazyLoadEvent>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() customSort = new EventEmitter<SortEvent>();

  @ContentChildren(AcanCellDirective)
  cellTemplates!: QueryList<AcanCellDirective>;

  @ViewChild('acanPTable') pTable?: Table;

  private cellMap = new Map<string, TemplateRef<unknown>>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** Delegates to the underlying p-table reset (first page, clears sort, re-fires lazy load). */
  reset(): void {
    this.pTable?.reset();
  }

  ngAfterContentInit(): void {
    this.rebuildCellMap();
    this.cellTemplates?.changes?.subscribe(() => {
      this.rebuildCellMap();
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  private rebuildCellMap(): void {
    this.cellMap.clear();
    (this.cellTemplates || []).forEach((dir) => {
      if (dir.templateKey) {
        this.cellMap.set(dir.templateKey, dir.template);
      }
    });
  }

  cellTemplate(col: AcanColumn): TemplateRef<unknown> | null {
    const key = col.templateKey || col.field;
    return this.cellMap.get(key) || null;
  }

  get columns(): AcanColumn[] {
    return (this.config?.columns || []).filter((c) => !c.hidden);
  }

  get lazy(): boolean {
    return this.config?.lazy !== false;
  }

  get paginator(): boolean {
    return this.config?.paginator !== false;
  }

  get rows(): number {
    return this.config?.rows || 10;
  }

  get rowsPerPageOptions(): number[] {
    return this.config?.rowsPerPageOptions || DEFAULT_ROWS_PER_PAGE_OPTIONS;
  }

  get dataKey(): string {
    return this.config?.dataKey || '_id';
  }

  get rowHover(): boolean {
    return this.config?.rowHover !== false;
  }

  get loading(): boolean {
    return !!this.config?.loading && !this.config?.skeleton;
  }

  get showSkeleton(): boolean {
    return !!this.config?.loading && !!this.config?.skeleton;
  }

  get skeletonRowIndexes(): number[] {
    const n = this.config?.skeletonRows || 5;
    return Array.from({ length: n }, (_, i) => i);
  }

  get selectionMode(): any {
    const mode = this.config?.selectionMode;
    if (mode === 'single' || mode === 'multiple') {
      return mode;
    }
    // Explicit opt-out for embeds / line-item grids
    if (mode === null || mode === 'none') {
      return null;
    }
    // Default: multi-select on all list tables
    return 'multiple';
  }

  get showCurrentPageReport(): boolean {
    return !!this.config?.showCurrentPageReport;
  }

  get currentPageReportTemplate(): string {
    return (
      this.config?.currentPageReportTemplate ||
      'Showing {first} to {last} of {totalRecords}'
    );
  }

  get sortField(): string {
    return this.config?.sortField || '';
  }

  get sortOrder(): number {
    return this.config?.sortOrder ?? 1;
  }

  get defaultSortOrder(): number {
    return this.config?.defaultSortOrder ?? 1;
  }

  get isCustomSort(): boolean {
    return !!this.config?.customSort;
  }

  get emptyMessage(): string {
    return this.config?.emptyMessage || 'No records found';
  }

  get tableStyle(): Record<string, string> | null {
    return this.config?.tableStyle || null;
  }

  get actionsWidth(): string {
    if (this.config?.actionsWidth) {
      return this.config.actionsWidth;
    }
    return this.cellMap.has('actions') ? '4.5rem' : '';
  }

  get showActionsColumn(): boolean {
    return !!this.config?.actionsWidth || this.cellMap.has('actions');
  }

  get actionsTemplate(): TemplateRef<unknown> | null {
    return this.cellMap.get('actions') || null;
  }

  /** Optional `<ng-template acanCell="empty">` replaces the default empty state. */
  get emptyTemplate(): TemplateRef<unknown> | null {
    return this.cellMap.get('empty') || null;
  }

  get emptyColspan(): number {
    return (
      this.columns.length +
        (this.selectionMode ? 1 : 0) +
        (this.showActionsColumn ? 1 : 0) || 1
    );
  }

  get tableStyleClass(): string {
    const extra = (this.config?.styleClass || '')
      .split(/\s+/)
      .filter((c) => c && c !== 'pp-flat-table' && c !== 'pp-table');
    if (this.config?.density === 'comfortable') {
      extra.push('pp-table--comfortable');
    }
    return ['pp-table', ...extra].join(' ');
  }

  columnClass(col: AcanColumn): string {
    const alignClass =
      col.align === 'money'
        ? 'pp-money'
        : col.align === 'left'
          ? 'pp-left'
          : col.align === 'center' || !col.align
            ? col.cssClass?.includes('pp-')
              ? ''
              : 'pp-center'
            : 'pp-center';
    return [alignClass, col.cssClass || ''].filter(Boolean).join(' ');
  }

  onRowClick(row: any, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.closest(
        'p-tablecheckbox, p-tableradiobutton, .p-checkbox, .p-radiobutton, button, a, .pp-btn--icon'
      )
    ) {
      return;
    }
    this.rowClick.emit(row);
  }

  onSelectionChange(value: any): void {
    this.selectionChange.emit(value);
  }

  onSort(event: SortEvent): void {
    this.customSort.emit(event);
  }

  cellValue(row: Record<string, unknown>, field: string): unknown {
    if (!field) {
      return undefined;
    }
    return field.split('.').reduce((acc: any, key) => acc?.[key], row);
  }
}
